import { useState, useEffect, useRef } from "react";
import { Transition } from "@headlessui/react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useGlobalSearch } from "../../hooks/useGlobalSearch";

// Section definitions shared with the /search results page: heading, icon and
// how each result type renders and navigates.
const SEARCH_SECTIONS = [
  {
    key: "sites",
    label: "Sites",
    icon: MapPinIcon,
    primaryText: (item) => item.site_name,
    secondaryText: (item) => item.street,
    getPath: (item) => `/sites/${item.id}`,
  },
  {
    key: "customers",
    label: "Customers",
    icon: BuildingStorefrontIcon,
    primaryText: (item) => item.customer_name,
    secondaryText: () => null,
    getPath: (item) => `/customers/?open=${item.id}`,
  },
  {
    key: "users",
    label: "Users",
    icon: UsersIcon,
    primaryText: (item) =>
      `${item.first_name || ""} ${item.last_name || ""}`.trim(),
    secondaryText: (item) => item.email,
    getPath: (item) => `/settings/users/?open=${item.id}`,
  },
];

const DROPDOWN_LIMIT = 5;

function SearchSectionHeading({ section }) {
  const Icon = section.icon;
  return (
    <div className="flex items-center gap-x-1.5 pb-2">
      <Icon
        className="h-4 w-4 text-gray-500 dark:text-gray-400"
        aria-hidden="true"
      />
      <p className="text-xsm uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {section.label}
      </p>
    </div>
  );
}

function SearchResultItem({ section, item, onSelect }) {
  const secondary = section.secondaryText(item);
  return (
    <div
      className="pl-2 pr-2 py-2 -ml-2 -mr-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer flex items-center justify-between gap-x-3 hover:bg-gray-50 dark:hover:bg-white/[0.06] rounded-md"
      onClick={() => onSelect(section.getPath(item))}
    >
      <span className="text-gray-800 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis">
        {section.primaryText(item)}
      </span>
      {secondary ? (
        <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
          {secondary}
        </span>
      ) : null}
    </div>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const { results, totalCount, isLoading, ensureData } =
    useGlobalSearch(query);

  const hasQuery = query.trim().length > 0;

  // Keep the input in sync with the /search results page.
  useEffect(() => {
    if (location.pathname.startsWith("/search")) {
      setQuery(searchParams.get("q") || "");
    }
  }, [location.pathname, searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const goToResults = () => {
    setIsOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleSelect = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    } else if (event.key === "Enter" && hasQuery) {
      goToResults();
    }
  };

  const handleClear = () => {
    setQuery("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full max-w-md" ref={containerRef}>
      <MagnifyingGlassIcon
        className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="text"
        placeholder="Search"
        aria-label="Search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(event.target.value.trim().length > 0);
        }}
        onFocus={(event) => {
          ensureData();
          if (hasQuery) setIsOpen(true);
          // Place the caret at the end, ready to keep typing. Deferred so it
          // runs after the browser positions the caret from the click itself.
          const input = event.target;
          requestAnimationFrame(() => {
            const length = input.value.length;
            input.setSelectionRange(length, length);
          });
        }}
        onKeyDown={handleKeyDown}
        className="block w-full rounded-md border-0 bg-white dark:bg-gray-900 py-2.5 pl-10 pr-9 text-sm leading-none font-normal text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
      />
      {query.length > 0 && (
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-150"
          onClick={handleClear}
          aria-label="Clear search"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      <Transition
        show={isOpen && hasQuery}
        enter="transition ease-out duration-100 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
        className="absolute left-0 top-full mt-2 z-50 w-full origin-top divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none"
      >
        <div className="p-4 max-h-96 overflow-y-auto">
          {isLoading ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Searching...
            </p>
          ) : totalCount === 0 ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              No results found
            </p>
          ) : (
            SEARCH_SECTIONS.map((section) => {
              const items = results[section.key];
              if (items.length === 0) return null;
              return (
                <div key={section.key} className="pb-2 last:pb-0">
                  <SearchSectionHeading section={section} />
                  {items.slice(0, DROPDOWN_LIMIT).map((item) => (
                    <SearchResultItem
                      key={item.id}
                      section={section}
                      item={item}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              );
            })
          )}
        </div>
        <button
          className="block w-full text-left px-4 py-3 text-sm text-brand-primary hover:bg-gray-50 dark:hover:bg-white/[0.06] rounded-b-md"
          onClick={goToResults}
        >
          View all results
        </button>
      </Transition>
    </div>
  );
}

export { GlobalSearch, SEARCH_SECTIONS, SearchSectionHeading, SearchResultItem };
